import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { anomalyKeys } from '../api/anomalies'
import { apiRequest } from '../api/client'
import { ApiFailure, queryKeys } from '../api/queries'
import { Btn, Mark } from '../components/Btn'
import { Combobox } from '../components/Combobox'
import { ConfirmButton } from '../components/ConfirmButton'
import { FixBanner } from '../components/FixBanner'
import { Kv } from '../components/Kv'
import { Pill } from '../components/Pill'
import { Field, Sheet } from '../components/Sheet'
import { TableControls } from '../components/TableControls'
import { useTableView, type FilterGroup, type TableSearch } from '../components/useTableView'
import { Panel, Section, SectionHead, Wrap } from '../components/layout'
import { todayIso } from '../lib/date'
import { fieldError, formError, reportApiError, type FieldErrors } from '../lib/formErrors'
import { recentValues } from '../lib/recentValues'
import type { PillTone } from '../lib/status'
import { useFixRowRef, useFlagFix, useOpenFixedRow } from '../lib/useFlagFix'
import { usePlate } from '../lib/usePlate'
import { useVehicleReg } from '../routes'
import { AppShell } from '../shell/AppShell'
import { PageHead } from '../shell/PageHead'
import { useToast } from '../shell/Toast'
import { useAddOnArrival } from '../lib/useAddOnArrival'

type Status = 'Owned' | 'OnOrder' | 'ToOrder'

interface EquipmentItem {
  id: number
  name: string
  category: string | null
  purchasedDate: string | null
  sourceVendor: string | null
  cost: number | null
  storedAt: string | null
  status: Status
  notes: string | null
}

/**
 * The stock axis, and it is a fourth one.
 *
 * Not due-status and not integrity: "I own this" / "it is coming" / "I should buy it" is about a thing's
 * existence, not its urgency or its trustworthiness. It borrows the tones because a reader should not learn a
 * new colour language per screen, and it keeps its own labels because the words are what carry the meaning.
 */
const STATUS: Record<Status, { label: string; tone: PillTone }> = {
  Owned: { label: 'Owned', tone: 'ok' },
  OnOrder: { label: 'On order', tone: 'soon' },
  ToOrder: { label: 'To order', tone: 'plain' },
}

const money = (n: number) =>
  n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 })

const shortDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

/**
 * The equipment inventory — the kit that lives with the car.
 *
 * A list rather than a table: an item is a name and a few facts, and there are no figures to align down a
 * column. It groups by category because that is how a boot is packed and how you notice the jack is missing.
 */
export function EquipmentPage() {
  const reg = useVehicleReg()
  const plate = usePlate()
  const [editing, setEditing] = useState<EquipmentItem | 'new' | null>(null)

  // Arrived from the dashboard's quick add, which carries ?add=1 rather than mounting this sheet on the
  // dashboard. Opening it here is what makes that one press instead of two.
  useAddOnArrival(() => setEditing('new'))

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['vehicle', reg, 'equipment'] as const,
    queryFn: async () => {
      const result = await apiRequest<EquipmentItem[]>(`/api/vehicles/${encodeURIComponent(reg)}/equipment`)
      if (!result.ok) throw new ApiFailure(result.error)
      return result.value
    },
  })

  const items = data ?? []
  const owned = items.filter((i) => i.status === 'Owned')
  const spend = owned.reduce((sum, i) => sum + (i.cost ?? 0), 0)

  // Grouped by category, with the uncategorised last rather than under a made-up heading.
  const categories = [...new Set(items.map((i) => i.category ?? ''))].sort((a, b) =>
    a === '' ? 1 : b === '' ? -1 : a.localeCompare(b),
  )

  // History-derived vendor suggestions for the free-text "From" field.
  const sourceSuggestions = recentValues(items, (i) => i.sourceVendor).map((value) => ({ value }))

  // The inventory's filter strip — the same shared capability as fuel, expenses and tasks: status chips and a
  // category select, declared as predicates over the one `<DataTable>` seam. No sort control — the list stays
  // grouped by category, so an unfiltered view reads exactly as before. The Inventory stats above stay on the
  // full set; the filter narrows the item list only.
  const groups: FilterGroup<EquipmentItem>[] = useMemo(
    () => [
      {
        id: 'status',
        label: 'Status',
        render: 'chips',
        options: (['Owned', 'OnOrder', 'ToOrder'] as Status[]).map((s) => ({
          id: s,
          label: STATUS[s].label,
          test: (i: EquipmentItem) => i.status === s,
        })),
      },
      {
        id: 'category',
        label: 'Category',
        render: 'select',
        // Every category that exists, even one currently filtered out by status — the select offers the whole
        // list; the empty '' (uncategorised) is not a pickable category, only a heading.
        options: categories
          .filter((c) => c !== '')
          .map((c) => ({ id: c, label: c, test: (i: EquipmentItem) => (i.category ?? '') === c })),
      },
    ],
    [categories],
  )

  // `notes` and `storedAt` are searched though neither has a column: "what's in the boot?" is a real question
  // the data can answer, and "do I already own one" is usually answered by something filed where you did not
  // look. A row can therefore match on text that is not on screen — accepted, and the point of the feature.
  const search: TableSearch<EquipmentItem> = useMemo(
    () => ({
      label: 'Search',
      fields: (i) => [i.name, i.notes, i.storedAt, i.sourceVendor, i.category],
    }),
    [],
  )

  const view = useTableView(items, { groups, search })

  // Arrived from the integrity queue's "Fix this": open the item the flag names, seeded for edit, and mark
  // its row. `EquipmentCostWithoutDate` is the one detector that lands here.
  const { flag, clear } = useFlagFix(reg, 'EquipmentItem')
  useOpenFixedRow(flag?.entityId, data, (i) => i.id, setEditing)
  const fixRowRef = useFixRowRef(flag?.entityId)

  // The categories actually present in the filtered rows, so a group filtered away does not render an empty
  // heading. Uncategorised sorts last, as in the unfiltered list.
  const visibleCategories = [...new Set(view.rows.map((i) => i.category ?? ''))].sort((a, b) =>
    a === '' ? 1 : b === '' ? -1 : a.localeCompare(b),
  )

  return (
    <AppShell
      scope={{ kind: 'vehicle', reg }}
      current="equipment"
      center={{ kind: 'action', icon: 'plus', label: 'Add item', onClick: () => setEditing('new') }}
      footer={
        <>
          What lives with the car, what is coming, and what is still on the list. <b>To order</b> is not a task:
          a task is work on the vehicle, and this is a shopping list — the two are different questions and get
          different screens.
        </>
      }
    >
      <PageHead
        eyebrow="Equipment · what lives with the car"
        title="Equipment"
        plate={plate}
        pmeta={
          data === undefined ? undefined : (
            <>
              <b>{owned.length} owned</b> · {items.filter((i) => i.status !== 'Owned').length} outstanding
              <br />
              {spend > 0 ? `${money(spend)} of kit` : 'nothing costed yet'}
            </>
          )
        }
      />

      {isError ? (
        <Section last>
          <Wrap>
            <Panel className="pad">
              <h2 className="panel-title">The inventory could not be loaded</h2>
              <p className="panel-empty">{error instanceof Error ? error.message : 'The request failed.'}</p>
              <button className="btn" type="button" onClick={() => void refetch()}>
                Try again
              </button>
            </Panel>
          </Wrap>
        </Section>
      ) : isPending || data === undefined ? (
        <Section last>
          <Wrap>
            <Panel className="pad">
              <p className="panel-empty">Loading…</p>
            </Panel>
          </Wrap>
        </Section>
      ) : (
        <>
          {items.length > 0 && (
            <Section>
              <Wrap>
                <SectionHead title="Inventory" rule={<>owned, on order, to order</>} />
                <Panel className="stats four num">
                  <Kv label="Owned" value={String(owned.length)} note="in the car or the garage" />
                  <Kv
                    label="On order"
                    value={String(items.filter((i) => i.status === 'OnOrder').length)}
                    note="bought, not arrived"
                  />
                  <Kv
                    label="To order"
                    value={String(items.filter((i) => i.status === 'ToOrder').length)}
                    note="still on the list"
                  />
                  <Kv label="Kit value" value={spend > 0 ? money(spend) : '—'} note="owned items with a cost" />
                </Panel>
              </Wrap>
            </Section>
          )}

          <Section last>
            <Wrap>
              <SectionHead
                title="Items"
                rule={<>grouped by category</>}
                link={<Mark onClick={() => setEditing('new')}>Add item</Mark>}
              />

              {/* Below the head, so the banner sits against the list it is about. */}
              {flag !== null && <FixBanner flag={flag} reg={reg} onDismiss={clear} />}
              {items.length === 0 ? (
                <Panel>
                  <p className="panel-empty">
                    Nothing listed. This is the kit that lives with the car — a jack, a tow rope, the OBD
                    reader — and what you have noticed you are missing.
                  </p>
                </Panel>
              ) : (
                <>
                  <TableControls view={view} noun="items" />
                  {view.count === 0 ? (
                    // A filter that matched nothing — distinct from the empty inventory above.
                    <Panel>
                      <p className="panel-empty">No items match this filter. Clear it to see all {view.total}.</p>
                    </Panel>
                  ) : (
                    visibleCategories.map((cat) => (
                      <Panel key={cat || 'uncategorised'} className="eqgroup">
                        <div className="eqhead">{cat || 'Uncategorised'}</div>
                        <ul className="eqlist">
                          {view.rows
                            .filter((i) => (i.category ?? '') === cat)
                            .map((i) => (
                              <li
                                key={i.id}
                                className={i.id === flag?.entityId ? 'is-fix' : undefined}
                                ref={fixRowRef(i.id)}
                              >
                                <Pill tone={STATUS[i.status].tone}>{STATUS[i.status].label}</Pill>
                                <span className="eqname">
                                  {i.name}
                                  {i.storedAt !== null && <em>{i.storedAt}</em>}
                                </span>
                                <span className="eqmeta num">
                                  {i.cost !== null && money(i.cost)}
                                  {i.purchasedDate !== null && ` · ${shortDate(i.purchasedDate)}`}
                                  {i.sourceVendor !== null && ` · ${i.sourceVendor}`}
                                </span>
                                <Mark onClick={() => setEditing(i)}>Edit</Mark>
                              </li>
                            ))}
                        </ul>
                      </Panel>
                    ))
                  )}
                </>
              )}
            </Wrap>
          </Section>
        </>
      )}

      <EquipmentSheet item={editing} onClose={() => setEditing(null)} reg={reg} sourceSuggestions={sourceSuggestions} />
    </AppShell>
  )
}

function EquipmentSheet({
  item,
  onClose,
  reg,
  sourceSuggestions,
}: {
  item: EquipmentItem | 'new' | null
  onClose: () => void
  reg: string
  sourceSuggestions: { value: string }[]
}) {
  const existing = item !== 'new' && item !== null ? item : null
  const [v, setV] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<FieldErrors>({})
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const get = (k: string, fallback = '') => v[k] ?? fallback
  const set = (k: string, value: string) => setV((p) => ({ ...p, [k]: value }))

  /**
   * Everything an equipment write moves.
   *
   * The anomaly queue is on this list because an item's cost only reaches spend through a mirrored expense,
   * and `AnomalyScanner` raises — or retracts — `EquipmentCostWithoutDate` inside the same transaction.
   * Without it, supplying the missing purchase date would leave the flag on screen and the garage card still
   * counting it, which is exactly the staleness the queue exists to report.
   */
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['vehicle', reg, 'equipment'] })
    await queryClient.invalidateQueries({ queryKey: anomalyKeys.all(reg) })
    await queryClient.invalidateQueries({ queryKey: queryKeys.vehicleSummary(reg) })
    await queryClient.invalidateQueries({ queryKey: queryKeys.garage })
  }

  // The one field the server can flag on an item — anything else it returns falls to the footer banner.
  const FIELD_KEYS = ['name'] as const

  /**
   * Whether this item's cost is money or an estimate — the client's read of the domain's
   * `EquipmentRules.CostIsSpend`. Only "To order" is a plan; "On order" is paid for and on its way.
   *
   * Written as "not ToOrder" for the same reason the server is, so a fourth status defaults to counting.
   */
  const isSpend = get('status', existing?.status ?? 'Owned') !== 'ToOrder'

  // An item needs a name; everything else is optional. Checked here so the answer is instant and beside the field.
  const validate = (): FieldErrors => {
    const e: FieldErrors = {}
    if (get('name', existing?.name ?? '').trim() === '') e['name'] = ['What is it?']
    return e
  }

  const submit = () => {
    const found = validate()
    setErrors(found)
    if (Object.keys(found).length === 0) mutation.mutate()
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const cost = get('cost', existing?.cost?.toString() ?? '')
      const body = {
        name: get('name', existing?.name ?? ''),
        status: get('status', existing?.status ?? 'Owned'),
        category: get('category', existing?.category ?? '') || null,
        purchasedDate: get('purchasedDate', existing === null ? todayIso() : (existing.purchasedDate ?? '')) || null,
        sourceVendor: get('sourceVendor', existing?.sourceVendor ?? '') || null,
        cost: cost === '' ? null : Number(cost),
        storedAt: get('storedAt', existing?.storedAt ?? '') || null,
        notes: get('notes', existing?.notes ?? '') || null,
      }
      const result = await apiRequest<EquipmentItem>(
        existing === null
          ? `/api/vehicles/${encodeURIComponent(reg)}/equipment`
          : `/api/vehicles/${encodeURIComponent(reg)}/equipment/${existing.id}`,
        {
          method: existing === null ? 'POST' : 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      )
      if (!result.ok) throw new ApiFailure(result.error)
      return result.value
    },
    onSuccess: async () => {
      await invalidate()
      toast(existing === null ? 'Item added' : 'Item saved')
      setV({})
      setErrors({})
      onClose()
    },
    onError: (e) => setErrors(reportApiError(e, FIELD_KEYS)),
  })

  const remove = useMutation({
    mutationFn: async () => {
      if (existing === null) return
      const result = await apiRequest<null>(`/api/vehicles/${encodeURIComponent(reg)}/equipment/${existing.id}`, {
        method: 'DELETE',
      })
      if (!result.ok) throw new ApiFailure(result.error)
    },
    onSuccess: async () => {
      await invalidate()
      toast('Item removed from the inventory')
      setV({})
      onClose()
    },
    onError: (e) => setErrors(reportApiError(e, FIELD_KEYS)),
  })

  return (
    <Sheet
      open={item !== null}
      onClose={onClose}
      title={existing === null ? 'Add item' : 'Edit item'}
      subtitle="the kit that lives with the car"
      onSubmit={submit}
      footer={
        <>
          {existing !== null && (
            <ConfirmButton onConfirm={() => remove.mutate()} pending={remove.isPending} />
          )}
          <Btn type="submit" onClick={() => {}}>
            {mutation.isPending ? 'Saving…' : 'Save item'}
          </Btn>
        </>
      }
    >
      <Field label="Name" wide error={fieldError(errors, 'name')}>
        {(p) => (
          <input
            type="text"
            placeholder="Scissor jack"
            value={get('name', existing?.name ?? '')}
            onChange={(e) => set('name', e.target.value)}
            {...p}
          />
        )}
      </Field>

      <Field label="Status" hint={isSpend ? undefined : 'a price here is an estimate, not spend'}>
        {(p) => (
          <select
            value={get('status', existing?.status ?? 'Owned')}
            onChange={(e) => set('status', e.target.value)}
            {...p}
          >
            <option value="Owned">Owned</option>
            <option value="OnOrder">On order</option>
            <option value="ToOrder">To order</option>
          </select>
        )}
      </Field>

      <Field label="Category" hint="how the list groups">
        {(p) => (
          <input
            type="text"
            placeholder="Recovery"
            value={get('category', existing?.category ?? '')}
            onChange={(e) => set('category', e.target.value)}
            {...p}
          />
        )}
      </Field>

      <Field label="Stored at">
        {(p) => (
          <input
            type="text"
            placeholder="Boot floor"
            value={get('storedAt', existing?.storedAt ?? '')}
            onChange={(e) => set('storedAt', e.target.value)}
            {...p}
          />
        )}
      </Field>

      <Field
        label="Cost £"
        hint={isSpend ? 'what it cost' : 'what you expect it to cost — an estimate, counted nowhere'}
      >
        {(p) => (
          <input
            type="text"
            inputMode="decimal"
            placeholder="24.99"
            value={get('cost', existing?.cost?.toString() ?? '')}
            onChange={(e) => set('cost', e.target.value)}
            {...p}
          />
        )}
      </Field>

      {/*
        The date is only asked for once the money is real. It used to default to today on every new item
        whatever its status, which is how a shopping-list estimate acquired a purchase date and turned into a
        genuine Tools/Equipment expense in spend, cost-per-mile and the budget.

        The fallback is evaluated per render, so switching the status select fills or clears it live — until
        the field is touched, after which `v.purchasedDate` wins and the typed value stands.
      */}
      <Field
        label="Bought"
        hint={isSpend ? 'the date the money went' : 'not yet bought — no date needed'}
      >
        {(p) => (
          <input
            type="date"
            value={get(
              'purchasedDate',
              existing === null ? (isSpend ? todayIso() : '') : (existing.purchasedDate ?? ''),
            )}
            onChange={(e) => set('purchasedDate', e.target.value)}
            {...p}
          />
        )}
      </Field>

      <Field label="From">
        {(p) => (
          <Combobox
            {...p}
            value={get('sourceVendor', existing?.sourceVendor ?? '')}
            onChange={(val) => set('sourceVendor', val)}
            suggestions={sourceSuggestions}
            placeholder="Halfords"
          />
        )}
      </Field>

      <Field label="Notes" wide>
        {(p) => (
          <input
            type="text"
            value={get('notes', existing?.notes ?? '')}
            onChange={(e) => set('notes', e.target.value)}
            {...p}
          />
        )}
      </Field>

      {formError(errors) !== undefined && (
        <div className="field wide">
          <span className="hint err" role="alert">
            {formError(errors)}
          </span>
        </div>
      )}
    </Sheet>
  )
}
